import { createSlice } from '@reduxjs/toolkit';
import { getCompositeKey } from '../../discussions/utils/discussionUtil';
import {
  fetchDiscussionUpvoteStatus,
  toggleUpvoteBlogDiscussion,
} from '../actions/discussionUpvoteActions';

const initialState: DiscussionUpvoteState = {
  byBlogAndDiscussion: {},
};

const discussionUpvoteSlice = createSlice({
  name: 'discussionUpvote',
  initialState,
  reducers: {},
  extraReducers: builder => {
    // Logic for fetching the UPVOTE STATUS
    builder
      .addCase(fetchDiscussionUpvoteStatus.pending, (state, action) => {
        const { contentId, discussionId } = action.meta.arg;
        const key = getCompositeKey(contentId, discussionId);

        if (state.byBlogAndDiscussion[key]) {
          // Preserve existing values so the UI doesn't flash while fetching
          state.byBlogAndDiscussion[key].loading = true;
          state.byBlogAndDiscussion[key].error = null;
        } else {
          state.byBlogAndDiscussion[key] = {
            contentId,
            total_upvotes: 0,
            user_has_upvoted: false,
            loading: true,
            error: null,
          };
        }
      })
      .addCase(fetchDiscussionUpvoteStatus.fulfilled, (state, { payload }) => {
        // 💡 FIX: This case was missing/incorrectly duplicated.
        // It's assumed the payload data property holds the necessary status fields
        const incomingData = payload.data;
        const key = getCompositeKey(
          incomingData.contentId,
          payload.discussionId
        );

        state.byBlogAndDiscussion[key] = {
          // Map the returned data, ensuring contentId is set correctly.
          contentId: incomingData.contentId,
          total_upvotes: incomingData.total_upvotes,
          user_has_upvoted: incomingData.user_has_upvoted,
          loading: false,
          error: null,
        };
      })
      .addCase(fetchDiscussionUpvoteStatus.rejected, (state, action) => {
        const { contentId, discussionId } = action.meta.arg;
        const key = getCompositeKey(contentId, discussionId);

        if (state.byBlogAndDiscussion[key]) {
          state.byBlogAndDiscussion[key].loading = false;
          state.byBlogAndDiscussion[key].error =
            action.error.message || 'Failed to fetch upvotes';
        } else {
          state.byBlogAndDiscussion[key] = {
            contentId,
            total_upvotes: 0,
            user_has_upvoted: false,
            loading: false,
            error: action.error.message || 'Failed to fetch upvotes',
          };
        }
      });

    // Logic for TOGGLING the upvote (PATCH request)
    builder
      .addCase(toggleUpvoteBlogDiscussion.pending, (state, action) => {
        const { contentId, discussionId } = action.meta.arg;
        const key = getCompositeKey(contentId, discussionId);

        if (!state.byBlogAndDiscussion[key]) {
          state.byBlogAndDiscussion[key] = {
            contentId,
            total_upvotes: 0,
            user_has_upvoted: false,
            loading: false,
            error: null,
          };
        }
        // Optimistic update: flip immediately
        const entry = state.byBlogAndDiscussion[key];
        entry.user_has_upvoted = !entry.user_has_upvoted;
        entry.total_upvotes = entry.user_has_upvoted
          ? entry.total_upvotes + 1
          : entry.total_upvotes - 1;
        entry.loading = true;
        entry.error = null;
      })
      .addCase(toggleUpvoteBlogDiscussion.fulfilled, (state, action) => {
        const { contentId, discussionId } = action.meta.arg;
        const key = getCompositeKey(contentId, discussionId);
        const { total_upvotes, user_has_upvoted } = action.payload;

        if (state.byBlogAndDiscussion[key]) {
          state.byBlogAndDiscussion[key].total_upvotes = total_upvotes;
          state.byBlogAndDiscussion[key].user_has_upvoted = user_has_upvoted;
          state.byBlogAndDiscussion[key].loading = false;
          state.byBlogAndDiscussion[key].error = null;
        }
      })
      .addCase(toggleUpvoteBlogDiscussion.rejected, (state, action) => {
        const { contentId, discussionId } = action.meta.arg;
        const key = getCompositeKey(contentId, discussionId);

        if (state.byBlogAndDiscussion[key]) {
          // Revert optimistic update
          const entry = state.byBlogAndDiscussion[key];
          entry.user_has_upvoted = !entry.user_has_upvoted;
          entry.total_upvotes = entry.user_has_upvoted
            ? entry.total_upvotes + 1
            : entry.total_upvotes - 1;
          entry.loading = false;
          entry.error = action.error.message || 'Failed to toggle upvote';
        }
      });
  },
});

export default discussionUpvoteSlice.reducer;
